// V2 Campaigns list — intro campaigns only. Articles live under /v2/articles;
// keeping them off this page enforces the split: campaigns fill the funnel,
// articles drive engagement.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ActionIcon,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Menu,
  Modal,
  Paper,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCopy, IconDots, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import useApi from '../hooks/useApi';
import {
  duplicateCampaign,
  deleteCampaign,
  fetchCampaign,
  fetchCampaigns,
  fetchLeads,
} from '../services/api';

// Funnel stages we visualise on intro cards, in flow order.
const STAGES = ['prospect', 'contacted', 'clicked', 'booked'];

const STAGE_COLORS = {
  prospect: 'var(--mantine-color-sage-1)',
  contacted: 'var(--mantine-color-sage-3)',
  clicked: 'var(--mantine-color-sage-5)',
  booked: 'var(--mantine-color-sage-7)',
};

function classifyStatus(campaign) {
  const raw = String(campaign?.status || '').toLowerCase();
  if (raw === 'archived') return 'deleted';
  if (raw === 'paused') return 'paused';
  if (raw === 'completed' || raw === 'done') return 'done';
  if (raw === 'active' || raw === 'running') return 'running';
  return 'draft';
}

function statusLabel(status) {
  return status;
}

function statusDotColor(status) {
  if (status === 'running') return 'var(--mantine-color-sage-6)';
  if (status === 'paused') return 'var(--aviary-muted)';
  if (status === 'done') return 'var(--aviary-muted)';
  return 'var(--aviary-disabled)';
}

function channelLine(channel) {
  const c = String(channel || '').toLowerCase();
  if (c === 'linkedin') return 'LinkedIn + email';
  if (c === 'email') return 'Email';
  if (c === 'whatsapp') return 'WhatsApp + SMS';
  if (c === 'sms') return 'SMS';
  return c;
}

function shortDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

function bucketLeadsByCampaign(leads) {
  const byId = new Map();
  for (const lead of leads || []) {
    const cid = String(lead.campaignId || '');
    if (!cid) continue;
    const stage = String(lead.stage || 'prospect').toLowerCase();
    let bucket = 'prospect';
    if (stage === 'contacted' || stage === 'qualified') bucket = 'contacted';
    else if (stage === 'clicked' || stage === 'hot' || stage === 'connected') bucket = 'clicked';
    else if (stage === 'booked' || stage === 'closed') bucket = 'booked';
    if (!byId.has(cid)) byId.set(cid, { prospect: 0, contacted: 0, clicked: 0, booked: 0, total: 0 });
    const row = byId.get(cid);
    row[bucket] += 1;
    row.total += 1;
  }
  return byId;
}

function FunnelBar({ counts }) {
  const total = STAGES.reduce((n, s) => n + (counts?.[s] || 0), 0);
  if (total === 0) {
    return (
      <Box style={{ height: 10, borderRadius: 6, background: 'var(--aviary-disabled)' }} />
    );
  }
  return (
    <Group gap={0} wrap="nowrap" style={{ height: 10, borderRadius: 6, overflow: 'hidden' }}>
      {STAGES.map((stage) => {
        const n = counts?.[stage] || 0;
        if (n === 0) return null;
        const pct = (n / total) * 100;
        return (
          <Box
            key={stage}
            style={{
              width: `${pct}%`,
              height: '100%',
              background: STAGE_COLORS[stage],
            }}
          />
        );
      })}
    </Group>
  );
}

function StageCountsLine({ counts }) {
  return (
    <Group gap="lg">
      {STAGES.map((stage) => {
        const n = counts?.[stage] || 0;
        const isBooked = stage === 'booked';
        return (
          <Text
            key={stage}
            size="sm"
            style={{
              color: isBooked ? 'var(--mantine-color-sage-7)' : 'var(--aviary-muted)',
              fontWeight: isBooked ? 600 : 400,
            }}
          >
            {n} {stage}
          </Text>
        );
      })}
    </Group>
  );
}

function CampaignCard({
  campaign,
  counts,
  duplicatedFromName,
  onOpen,
  onDelete,
  onEdit,
  onDuplicate,
}) {
  const status = classifyStatus(campaign);
  const isPaused = status === 'paused';
  const isDraft = status === 'draft';
  const statusPill = isDraft ? 'draft' : (isPaused ? 'paused' : null);
  return (
    <Paper
      p="lg"
      radius="md"
      withBorder
      style={{
        background: isPaused ? 'var(--aviary-surface)' : '#fff',
        border: isDraft ? '1px dashed var(--mantine-color-yellow-6)' : undefined,
        cursor: 'pointer',
        opacity: isPaused ? 0.75 : 1,
      }}
      onClick={onOpen}
    >
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
            <Group gap="xs" align="center" wrap="nowrap">
              <Box
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: status === 'running' ? statusDotColor(status) : 'transparent',
                  border: status !== 'running' ? `2px solid ${statusDotColor(status)}` : 'none',
                  flexShrink: 0,
                }}
              />
              <Text
                fw={700}
                style={{ color: isPaused ? 'var(--aviary-muted)' : 'var(--aviary-ink)', fontSize: 18 }}
                truncate
              >
                {campaign.name || 'Untitled campaign'}
              </Text>
            </Group>
            {isPaused ? (
              <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
                nothing sends while paused
              </Text>
            ) : (
              <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
                {statusLabel(status)} · {channelLine(campaign.channel)}
                {campaign.createdAt ? ` · started ${shortDate(campaign.createdAt)}` : ''}
              </Text>
            )}
          </Stack>
          <Group gap="xs" wrap="nowrap" onClick={(e) => e.stopPropagation()}>
            {statusPill && (
              <Text
                size="xs"
                px="sm"
                py={4}
                style={{
                  color: isDraft ? 'var(--mantine-color-yellow-8)' : 'var(--aviary-muted)',
                  border: isDraft ? '1px solid var(--mantine-color-yellow-6)' : '1px solid var(--aviary-disabled)',
                  background: isDraft ? 'var(--mantine-color-yellow-0)' : 'transparent',
                  borderRadius: 999,
                }}
              >
                {statusPill}
              </Text>
            )}
            <Menu shadow="md" position="bottom-end" withArrow>
              <Menu.Target>
                <ActionIcon variant="subtle" color="gray" size="sm" aria-label="Campaign options">
                  <IconDots size={16} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconPencil size={14} />}
                  onClick={() => onEdit?.(campaign)}
                >
                  Edit campaign
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconCopy size={14} />}
                  onClick={() => onDuplicate?.(campaign)}
                >
                  Duplicate
                </Menu.Item>
                <Menu.Item
                  color="red"
                  leftSection={<IconTrash size={14} />}
                  onClick={() => onDelete?.(campaign)}
                >
                  Delete
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>

        {isDraft ? (
          <Text size="sm" style={{ color: 'var(--mantine-color-yellow-8)', lineHeight: 1.5 }}>
            {duplicatedFromName
              ? `Duplicated from "${duplicatedFromName}". Settings copied, no prospects yet — click Edit to run the wizard and launch.`
              : 'Duplicated. Settings copied, no prospects yet — click Edit to run the wizard and launch.'}
          </Text>
        ) : !isPaused ? (
          <>
            <FunnelBar counts={counts} />
            <StageCountsLine counts={counts} />
          </>
        ) : null}
      </Stack>
    </Paper>
  );
}

const INITIAL_VISIBLE = 5;

export default function AviaryV2CampsPage() {
  const api = useApi();
  const navigate = useNavigate();

  const [campaigns, setCampaigns] = useState(null);
  const [leadsByCampaign, setLeadsByCampaign] = useState(new Map());
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [campData, leadData] = await Promise.all([
          fetchCampaigns(api),
          fetchLeads(api).catch(() => []),
        ]);
        if (cancelled) return;
        const list = Array.isArray(campData) ? campData : (campData?.campaigns || []);
        const leads = Array.isArray(leadData) ? leadData : (leadData?.leads || []);

        const sorted = list.slice().sort((a, b) => {
          const at = new Date(a.updatedAt || a.createdAt || 0).getTime();
          const bt = new Date(b.updatedAt || b.createdAt || 0).getTime();
          return bt - at;
        });

        setCampaigns(sorted);
        setLeadsByCampaign(bucketLeadsByCampaign(leads));
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [api]);

  const visibleCampaigns = useMemo(() => {
    return (campaigns || []).filter((c) => classifyStatus(c) !== 'deleted');
  }, [campaigns]);

  const introNameById = useMemo(() => {
    const map = new Map();
    (campaigns || []).forEach((c) => map.set(String(c.id), c.name || 'Campaign'));
    return map;
  }, [campaigns]);

  const shown = showAll ? visibleCampaigns : visibleCampaigns.slice(0, INITIAL_VISIBLE);
  const hiddenCount = Math.max(0, visibleCampaigns.length - shown.length);

  async function handleConfirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteCampaign(api, deleteTarget.id);
      setCampaigns((prev) => (prev || []).map((c) => (
        c.id === deleteTarget.id ? { ...c, status: 'archived', archived: true } : c
      )));
      notifications.show({
        title: 'Campaign deleted',
        message: `"${deleteTarget.name || 'Campaign'}" moved to Deleted.`,
        color: 'sage',
      });
      setDeleteTarget(null);
    } catch (err) {
      notifications.show({
        title: 'Could not delete',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    } finally {
      setDeleting(false);
    }
  }

  async function handleDuplicateCampaign(campaign) {
    setDuplicatingId(campaign.id);
    try {
      const duplicated = await duplicateCampaign(api, campaign.id);
      const campaignId = duplicated?.id || '';
      const detail = campaignId ? await fetchCampaign(api, campaignId) : null;
      const hydrated = detail?.campaign || duplicated;
      setCampaigns((prev) => {
        const existing = Array.isArray(prev) ? prev : [];
        return [hydrated, ...existing];
      });
      notifications.show({
        title: 'Campaign duplicated',
        message: `Duplicated as "${hydrated?.name || 'Copy'}"`,
        color: 'sage',
      });
    } catch (err) {
      notifications.show({
        title: 'Could not duplicate',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    } finally {
      setDuplicatingId(null);
    }
  }

  if (error) {
    return (
      <Center py={80} px="lg">
        <Stack gap="sm" align="center">
          <Text fw={600} c="red">Couldn't load campaigns</Text>
          <Text size="sm" c="dimmed">{error}</Text>
        </Stack>
      </Center>
    );
  }

  if (campaigns === null) {
    return (
      <Center py={80}>
        <Loader color="sage" />
      </Center>
    );
  }

  return (
    <Box style={{ padding: '40px 24px 120px', maxWidth: 900, margin: '0 auto' }}>
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 28 }}>
            Campaigns
          </Text>
          <Button
            color="sage"
            leftSection={<IconPlus size={16} />}
            onClick={() => navigate('/v2?new=1')}
          >
            New campaign
          </Button>
        </Group>

        {shown.length === 0 ? (
          <Paper p="xl" radius="md" withBorder style={{ background: 'var(--aviary-surface)' }}>
            <Stack gap="sm" align="center">
              <Text style={{ color: 'var(--aviary-muted)' }}>
                No campaigns yet.
              </Text>
              <Button
                color="sage"
                variant="light"
                leftSection={<IconPlus size={14} />}
                onClick={() => navigate('/v2?new=1')}
              >
                Start your first campaign
              </Button>
            </Stack>
          </Paper>
        ) : (
          <Stack gap="md">
            {shown.map((campaign) => (
              <CampaignCard
                key={`intro-${campaign.id}`}
                campaign={campaign}
                counts={leadsByCampaign.get(String(campaign.id))}
                duplicatedFromName={introNameById.get(String(campaign.duplicatedFrom || '')) || null}
                onOpen={() => navigate(`/v2/campaigns/${campaign.id}`, {
                  state: { back: { path: '/v2/campaigns', label: 'Campaigns' } },
                })}
                onEdit={(c) => navigate(`/v2?mode=edit&campaignId=${encodeURIComponent(c.id)}`)}
                onDuplicate={(c) => {
                  if (duplicatingId) return;
                  handleDuplicateCampaign(c);
                }}
                onDelete={(c) => {
                  const total = Number(leadsByCampaign.get(String(c.id))?.total || 0);
                  setDeleteTarget({ ...c, prospectCount: total });
                }}
              />
            ))}

            {hiddenCount > 0 && (
              <UnstyledButton
                onClick={() => setShowAll(true)}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: 18,
                  borderRadius: 12,
                  border: '1px dashed var(--aviary-disabled)',
                  background: 'transparent',
                  textAlign: 'center',
                }}
              >
                <Text size="sm" style={{ color: 'var(--aviary-muted)', fontStyle: 'italic' }}>
                  {hiddenCount} more…
                </Text>
              </UnstyledButton>
            )}
          </Stack>
        )}

        <Text size="xs" ta="center" fs="italic" style={{ color: 'var(--aviary-muted)' }}>
          Edit reopens the wizard prefilled with current values (prospect callout excluded) · Duplicate copies settings + appends "Copy", lands as a draft
        </Text>
      </Stack>

      <Modal
        opened={Boolean(deleteTarget)}
        onClose={() => (deleting ? null : setDeleteTarget(null))}
        title={`Delete "${deleteTarget?.name || 'campaign'}"?`}
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            Delete "{deleteTarget?.name || 'campaign'}"? It and its {Number(deleteTarget?.prospectCount || 0)} prospects
            {' '}will be moved to the Deleted tab.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button color="red" onClick={handleConfirmDelete} loading={deleting}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
